import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Btn } from './index';
import Icon from './Icon';
import { programPassengerKey } from '../utils/programPlanning';
import { publishedVanStatus, publishedVanStatusLabel, vanNumber, vanPendingItems, vanReady } from '../utils/planningWorkspace';
import { peopleByRegion } from '../utils/vanBoard';
import './planning-board.css';

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;
const capacityOf = (group, reference) => (group.veiculo?.id ? Number(group.veiculo.capacidade) || 15 : reference || 15);
const formatTime = (date) => date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

function Seats({ count, capacity, label }) {
  const total = Math.max(count, capacity);
  return <div className="pb-seats" role="img" aria-label={label}>
    {Array.from({ length: total }, (_, index) => <span key={index}
      className={`pb-seat${index < Math.min(count, capacity) ? ' is-taken' : index >= capacity ? ' is-over' : ''}`} />)}
  </div>;
}
Seats.propTypes = { count: PropTypes.number.isRequired, capacity: PropTypes.number.isRequired, label: PropTypes.string.isRequired };

const Grip = () => <svg className="pb-grip" width="10" height="16" viewBox="0 0 10 16" aria-hidden="true">
  {[3, 8, 13].flatMap((y) => [2, 8].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.5" fill="currentColor" />))}
</svg>;

function statusOf(group) {
  const pending = vanPendingItems(group);
  if (!pending.length) return { label: 'Pronta para enviar', tone: 'ready' };
  if (!group.motorista?.id && !group.veiculo?.id) return { label: 'Falta motorista e veículo', tone: 'warn' };
  if (!group.motorista?.id) return { label: 'Falta motorista', tone: 'warn' };
  if (!group.veiculo?.id) return { label: 'Falta veículo', tone: 'warn' };
  return { label: pending[0], tone: 'bad' };
}

const HINT_KEY = 'rotaflex_quadro_vans_dica_fechada';
const readHintDismissed = () => { try { return localStorage.getItem(HINT_KEY) === '1'; } catch { return false; } };

export default function PlanningBoard({ groups, published = [], drivers, vehicles, referenceCapacity, loading, saveState, tripLabel,
  passengerTotal, orders = [], onDriver, onVehicle, onMove, onSendVans, onFinish, onEditPassengers, onRetrySave, onDetailed, onOpenPublished }) {
  const [selected, setSelected] = useState(() => new Set());
  const [addingVan, setAddingVan] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [dragOver, setDragOver] = useState(null);
  const dragKeys = useRef([]);
  const [showOrders, setShowOrders] = useState(false);
  const [hintDismissed, setHintDismissed] = useState(readHintDismissed);
  const dismissHint = () => { setHintDismissed(true); try { localStorage.setItem(HINT_KEY, '1'); } catch { /* preferência só desta sessão */ } };
  const keys = useMemo(() => new Set(groups.flatMap((group) => group.pessoas.map(programPassengerKey))), [groups]);
  // Passageiros que saíram da montagem (salvamento, edição) deixam a seleção.
  useEffect(() => {
    setSelected((previous) => {
      const next = new Set([...previous].filter((key) => keys.has(key)));
      return next.size === previous.size ? previous : next;
    });
  }, [keys]);

  const usedDrivers = [...groups.map((g) => Number(g.motorista?.id)), ...published.map((v) => Number(v.motorista?.id))];
  const usedVehicles = [...groups.map((g) => Number(g.veiculo?.id)), ...published.map((v) => Number(v.veiculo?.id))];
  const readyIndexes = groups.flatMap((group, index) => (vanReady(group) ? [index] : []));
  const toggle = (key) => setSelected((previous) => {
    const next = new Set(previous);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const toggleRegion = (people) => setSelected((previous) => {
    const next = new Set(previous);
    const regionKeys = people.map(programPassengerKey);
    const all = regionKeys.every((key) => next.has(key));
    regionKeys.forEach((key) => (all ? next.delete(key) : next.add(key)));
    return next;
  });
  const move = (destination) => {
    if (onMove([...selected], destination)) { setSelected(new Set()); setAddingVan(false); }
  };
  const addVan = () => (selected.size ? move('new') : setAddingVan(true));
  const vanOfKey = useMemo(() => new Map(groups.flatMap((group, index) => group.pessoas.map((person) => [programPassengerKey(person), String(index)]))), [groups]);

  // Arrastar e soltar: um nome (ou todos os marcados, se ele estiver marcado) ou uma região inteira.
  const startDrag = (event, draggedKeys) => {
    dragKeys.current = draggedKeys;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', draggedKeys.join(','));
    setDragging(true);
  };
  const endDrag = () => { dragKeys.current = []; setDragging(false); setDragOver(null); };
  const dropTarget = (target) => ({
    onDragOver: (event) => {
      if (!dragKeys.current.length) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      if (dragOver !== target) setDragOver(target);
    },
    onDragLeave: (event) => { if (!event.currentTarget.contains(event.relatedTarget)) setDragOver(null); },
    onDrop: (event) => {
      event.preventDefault();
      const draggedKeys = dragKeys.current.filter((key) => vanOfKey.get(key) !== target);
      endDrag();
      if (draggedKeys.length && onMove(draggedKeys, target)) { setSelected(new Set()); setAddingVan(false); }
    },
  });
  const personDragKeys = (key) => (selected.has(key) ? [...selected] : [key]);
  const removeVan = (index, destination) => {
    if (onMove(groups[index].pessoas.map(programPassengerKey), destination)) setRemoving(null);
  };
  // Divide mantendo regiões e bairros juntos: a segunda metade, na ordem exibida, vai para uma van nova.
  const splitVan = (group) => {
    const people = peopleByRegion(group.pessoas).flatMap((region) => region.people);
    onMove(people.slice(Math.ceil(people.length / 2)).map(programPassengerKey), 'new');
  };
  const vanTotal = groups.length + published.length;
  const allSent = groups.length === 0 && published.length > 0;
  // Envio e conclusão esperam o salvamento automático terminar.
  const settling = loading || saveState.status === 'saving';

  let saveText = 'As alterações são salvas automaticamente.';
  if (saveState.status === 'saving') saveText = 'Salvando…';
  else if (saveState.status === 'saved') saveText = `Salvo às ${formatTime(saveState.at)}.`;

  return <div className="pb">
    <div className="pb-context">
      <h2 className="pb-trip">{tripLabel}</h2>
      <p className="pb-count">
        {plural(passengerTotal, 'passageiro', 'passageiros')} em {plural(vanTotal, 'van', 'vans')}, de{' '}
        <button type="button" className="pb-link pb-inline-link" aria-expanded={showOrders} aria-controls="pb-orders" onClick={() => setShowOrders((value) => !value)}>
          {plural(orders.length, 'pedido', 'pedidos')}
        </button>
        <span className="pb-save" role="status" aria-live="polite">
          {saveState.status === 'error'
            ? <span className="pb-save-error"><Icon name="warning" size={16} /> Não foi possível salvar: {saveState.message}
              <Btn size="sm" variant="secondary" onClick={onRetrySave} disabled={loading}>Tentar de novo</Btn></span>
            : saveText}
        </span>
      </p>
      {showOrders && <ul id="pb-orders" className="pb-orders">
        {orders.map((order) => <li key={order.id}><strong>Pedido {order.codigo}</strong> {plural(order.passageiros, 'passageiro', 'passageiros')}</li>)}
      </ul>}
    </div>
    {allSent && <div className="pb-all-sent" role="status">
      <div><strong>Todas as vans foram enviadas.</strong> Acompanhe as respostas dos motoristas em Em operação.</div>
      <Btn onClick={() => onOpenPublished(published[0].id)}>Acompanhar envio</Btn>
    </div>}
    {!hintDismissed && !allSent && <p className="pb-hint">
      <span><strong>Arraste um nome ou uma região</strong> para outra van, ou marque as pessoas e escolha a van na barra de baixo.</span>
      <button type="button" className="pb-link" onClick={dismissHint}>Entendi</button>
    </p>}

    <div className="pb-grid">
      {groups.map((group, index) => {
        const number = vanNumber(group, index);
        const capacity = capacityOf(group, referenceCapacity);
        const status = statusOf(group);
        const over = group.pessoas.length > capacity;
        const isOver = dragOver === String(index);
        return <section key={group.planejamento_grupo_id || `nova-${index}`} className={`pb-van${dragging ? ' is-drop-candidate' : ''}${isOver ? ' is-drop-over' : ''}`} aria-labelledby={`pb-van-${index}`} {...dropTarget(String(index))}>
          {isOver && <div className="pb-drop-hint" aria-hidden="true">Solte aqui para levar para a Van {number}</div>}
          <header className="pb-van-head">
            <div className="pb-van-title"><h2 id={`pb-van-${index}`}>Van {number}</h2><span>{group.regiao}</span></div>
            <Seats count={group.pessoas.length} capacity={capacity} label={`${group.pessoas.length} passageiros para ${capacity} lugares`} />
            <p className={`pb-capacity${over ? ' is-over' : ''}`}>{over ? `${group.pessoas.length} pessoas para ${capacity} lugares` : `${group.pessoas.length} de ${capacity} lugares`}{group.veiculo?.id ? '' : ' (sem veículo escolhido)'}</p>
            <div className="pb-executors">
              <label>Motorista
                <select value={group.motorista?.id || ''} disabled={loading} aria-label={`Motorista da van ${number}`} onChange={(event) => onDriver(index, event.target.value)}>
                  <option value="">Escolher</option>
                  {group.motorista?.id && !drivers.some((driver) => Number(driver.id) === Number(group.motorista.id)) && <option value={group.motorista.id}>{group.motorista.nome || 'Motorista salvo'}</option>}
                  {drivers.map((driver) => <option key={driver.id} value={driver.id} disabled={usedDrivers.includes(Number(driver.id)) && Number(group.motorista?.id) !== Number(driver.id)}>{driver.nome}</option>)}
                </select>
              </label>
              <label>Veículo (lugares)
                <select value={group.veiculo?.id || ''} disabled={loading} aria-label={`Veículo da van ${number}`} onChange={(event) => onVehicle(index, event.target.value)}>
                  <option value="">Escolher</option>
                  {group.veiculo?.id && !vehicles.some((vehicle) => Number(vehicle.id) === Number(group.veiculo.id)) && <option value={group.veiculo.id}>{group.veiculo.placa || 'Veículo salvo'} · {group.veiculo.capacidade || 15}</option>}
                  {vehicles.map((vehicle) => <option key={vehicle.id} value={vehicle.id} disabled={usedVehicles.includes(Number(vehicle.id)) && Number(group.veiculo?.id) !== Number(vehicle.id)}>{vehicle.placa || vehicle.veiculo} · {vehicle.capacidade || 15}</option>)}
                </select>
              </label>
            </div>
            <span className={`pb-status is-${status.tone}`}>{status.label}</span>
          </header>
          <div className="pb-people">
            {peopleByRegion(group.pessoas).map((region) => {
              const all = region.people.every((person) => selected.has(programPassengerKey(person)));
              return <div key={region.name} className="pb-region">
                <div className="pb-region-head" draggable={!loading} onDragStart={(event) => startDrag(event, region.people.map(programPassengerKey))} onDragEnd={endDrag} title="Arraste para levar a região inteira">
                  <span className="pb-region-name"><Grip /><span>{region.name} <small>({region.people.length})</small></span></span>
                  <button type="button" className="pb-link pb-link-quiet" disabled={loading} aria-label={`${all ? 'Desmarcar' : 'Marcar'} ${region.name} na van ${number}`} onClick={() => toggleRegion(region.people)}>{all ? 'Desmarcar' : 'Marcar região'}</button>
                </div>
                {region.people.map((person) => {
                  const key = programPassengerKey(person);
                  const isSelected = selected.has(key);
                  return <button key={key} type="button" className={`pb-person${isSelected ? ' is-selected' : ''}`} aria-pressed={isSelected} disabled={loading} onClick={() => toggle(key)}
                    draggable={!loading} onDragStart={(event) => startDrag(event, personDragKeys(key))} onDragEnd={endDrag}>
                    <Grip />
                    <span className="pb-box" aria-hidden="true">{isSelected && <Icon name="check" size={13} />}</span>
                    <span className="pb-name">
                      <span className="pb-name-line">{person.nome}{person.bairro && <span className="pb-place"><span className="rf-visually-hidden">, </span>{person.bairro}</span>}</span>
                      {person.id_pedido_origem && <span className="pb-origin"><span className="rf-visually-hidden">, </span>PG {person.id_pedido_origem}</span>}
                    </span>
                  </button>;
                })}
              </div>;
            })}
          </div>
          {removing === index ? <footer className="pb-van-foot pb-remove" role="group" aria-label={`Excluir a van ${number}`}>
            <p><strong>Excluir a Van {number}.</strong> {group.pessoas.length === 1 ? 'Para qual van vai essa pessoa?' : `Para qual van vão as ${group.pessoas.length} pessoas?`}</p>
            <div className="pb-remove-targets">
              {groups.map((target, targetIndex) => targetIndex === index ? null : <Btn key={target.planejamento_grupo_id || `remover-${targetIndex}`} size="sm" variant="secondary" disabled={loading} onClick={() => removeVan(index, String(targetIndex))}>Van {vanNumber(target, targetIndex)} ({target.pessoas.length})</Btn>)}
              <Btn size="sm" variant="ghost" onClick={() => setRemoving(null)}>Cancelar</Btn>
            </div>
          </footer> : <footer className="pb-van-foot">
            <div className="pb-foot-links">
              <button type="button" className="pb-link" disabled={loading} onClick={() => onEditPassengers(index)}>Incluir ou retirar passageiros</button>
              {group.pessoas.length > 1 && <button type="button" className="pb-link" disabled={loading} aria-label={`Dividir a van ${number} em 2 vans`} onClick={() => splitVan(group)}>Dividir em 2 vans</button>}
              {groups.length > 1 && <button type="button" className="pb-link pb-link-danger" disabled={loading} aria-label={`Excluir a van ${number}`} onClick={() => setRemoving(index)}>Excluir van</button>}
            </div>
            <Btn size="sm" variant="secondary" disabled={settling || !vanReady(group)} onClick={() => onSendVans([index])} aria-label={`Enviar van ${number} ao motorista`}><Icon name="send" size={15} /> Enviar esta van</Btn>
          </footer>}
        </section>;
      })}

      {allSent ? null : addingVan ? <section className={`pb-van pb-new-van is-active${dragOver === 'new' ? ' is-drop-over' : ''}`} aria-labelledby="pb-new-van-title" {...dropTarget('new')}>
        <h2 id="pb-new-van-title">Nova van</h2>
        <p>Arraste para cá os nomes ou regiões que vão nela. Ou marque as pessoas e use <strong>Levar para a nova van</strong> na barra de baixo.</p>
        <Btn size="sm" variant="ghost" onClick={() => setAddingVan(false)}>Cancelar</Btn>
      </section> : <button type="button" className={`pb-van pb-new-van${dragOver === 'new' ? ' is-drop-over' : ''}`} disabled={loading} onClick={addVan} {...dropTarget('new')}>
        <Icon name="plus" size={22} />
        <strong>Adicionar van</strong>
        <span>{dragging ? 'Solte aqui para criar uma van' : selected.size ? `Leva ${plural(selected.size, 'pessoa selecionada', 'pessoas selecionadas')}` : 'Ou arraste nomes para cá'}</span>
      </button>}

      {published.map((van) => {
        const status = publishedVanStatus(van);
        const people = van.grupo?.pessoas || [];
        return <section key={van.id} className="pb-van is-published" aria-labelledby={`pb-sent-${van.id}`}>
          <header className="pb-van-head">
            <div className="pb-van-title"><h2 id={`pb-sent-${van.id}`}>Van {vanNumber(van.grupo)}</h2><span>{van.grupo?.regiao}</span></div>
            <p className="pb-capacity">{plural(people.length, 'passageiro', 'passageiros')}. {van.motorista?.nome || 'Motorista'}, {van.veiculo?.placa || 'veículo'}.</p>
            <span className={`pb-status is-${status === 'failed' ? 'bad' : status === 'sent' ? 'sent' : 'info'}`}>{publishedVanStatusLabel(van)}</span>
          </header>
          <div className="pb-people">
            {peopleByRegion(people).map((region) => <div key={region.name} className="pb-region">
              <div className="pb-region-head"><span>{region.name} <small>({region.people.length})</small></span></div>
              {region.people.map((person) => <div key={programPassengerKey(person)} className="pb-person is-static"><span className="pb-name">{person.nome}{person.bairro && <span className="pb-place"><span className="rf-visually-hidden">, </span>{person.bairro}</span>}</span></div>)}
            </div>)}
          </div>
          <footer className="pb-van-foot"><Btn size="sm" variant="secondary" onClick={() => onOpenPublished(van.id)}>Acompanhar van</Btn></footer>
        </section>;
      })}
    </div>

    {!allSent && <p className="pb-detailed"><button type="button" className="pb-link" onClick={onDetailed}>Abrir a montagem detalhada</button> para juntar vans, dividir em várias partes ou trocar o pedido de referência.</p>}

    {!allSent && <div className={`pb-bar${selected.size ? ' is-active' : ''}`}>
      {selected.size > 0 ? <div className="pb-bar-move">
        <strong className="pb-bar-title">{plural(selected.size, 'pessoa selecionada', 'pessoas selecionadas')}. Levar para:</strong>
        {addingVan && <Btn size="sm" disabled={loading} onClick={() => move('new')}><Icon name="plus" size={15} /> Levar para a nova van</Btn>}
        {groups.map((group, index) => <Btn key={group.planejamento_grupo_id || `destino-${index}`} size="sm" variant="secondary" disabled={loading} onClick={() => move(String(index))}>Van {vanNumber(group, index)} ({group.pessoas.length})</Btn>)}
        {!addingVan && <Btn size="sm" variant="secondary" disabled={loading} onClick={() => move('new')}><Icon name="plus" size={15} /> Nova van</Btn>}
        <Btn size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Limpar seleção</Btn>
      </div> : <div className="pb-bar-actions">
        <Btn variant="secondary" disabled={settling || !readyIndexes.length} onClick={() => onSendVans(readyIndexes)}
          aria-label={readyIndexes.length ? `Enviar agora ${plural(readyIndexes.length, 'van pronta', 'vans prontas')}` : 'Enviar agora'}>
          <Icon name="send" size={16} /> Enviar agora{readyIndexes.length ? <span className="pb-bar-count"> ({readyIndexes.length})</span> : null}
        </Btn>
        <Btn disabled={settling} onClick={onFinish}><Icon name="check" size={16} /> Concluir montagem</Btn>
      </div>}
    </div>}
  </div>;
}

PlanningBoard.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.object).isRequired,
  published: PropTypes.arrayOf(PropTypes.object),
  drivers: PropTypes.arrayOf(PropTypes.object).isRequired,
  vehicles: PropTypes.arrayOf(PropTypes.object).isRequired,
  referenceCapacity: PropTypes.number,
  loading: PropTypes.bool,
  saveState: PropTypes.shape({ status: PropTypes.string, at: PropTypes.instanceOf(Date), message: PropTypes.string }).isRequired,
  tripLabel: PropTypes.string.isRequired,
  passengerTotal: PropTypes.number.isRequired,
  orders: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), codigo: PropTypes.string, passageiros: PropTypes.number })),
  onDriver: PropTypes.func.isRequired,
  onVehicle: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onSendVans: PropTypes.func.isRequired,
  onFinish: PropTypes.func.isRequired,
  onEditPassengers: PropTypes.func.isRequired,
  onRetrySave: PropTypes.func.isRequired,
  onDetailed: PropTypes.func.isRequired,
  onOpenPublished: PropTypes.func.isRequired,
};
